export const DATA_SCIENCE_COURSES = [
  {
    id: 'data-science-fundamentals',
    title: 'Data Science Fundamentals',
    description: 'Master the complete data science pipeline from data collection to machine learning deployment.',
    level: 'beginner',
    totalDuration: '15-18 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Python basics', 'Basic statistics', 'Mathematical reasoning'],
    learningOutcomes: [
      'Master Python for data analysis',
      'Perform exploratory data analysis',
      'Build predictive models with scikit-learn',
      'Visualize data effectively',
      'Handle missing data and outliers',
      'Deploy machine learning models'
    ],
    modules: [
      {
        id: 'python-data-analysis',
        title: 'Python for Data Analysis',
        description: 'Master pandas, numpy, and matplotlib for data manipulation and visualization',
        duration: '150 minutes',
        difficulty: 'beginner',
        objectives: [
          'Master pandas DataFrame operations',
          'Perform statistical analysis with numpy',
          'Create effective visualizations',
          'Handle various data formats'
        ],
        content: `# Python for Data Analysis

## Essential Libraries

### NumPy - Numerical Computing
\`\`\`python
import numpy as np

# Array creation and operations
arr = np.array([1, 2, 3, 4, 5])
matrix = np.array([[1, 2], [3, 4], [5, 6]])

# Statistical operations
mean_val = np.mean(arr)
std_val = np.std(arr)
correlation = np.corrcoef(matrix, rowvar=False)

# Broadcasting and vectorization
result = arr * 2 + 10  # Vectorized operation
normalized = (arr - np.mean(arr)) / np.std(arr)
\`\`\`

### Pandas - Data Manipulation
\`\`\`python
import pandas as pd

# DataFrame creation
df = pd.DataFrame({
    'name': ['Alice', 'Bob', 'Charlie', 'Diana'],
    'age': [25, 30, 35, 28],
    'salary': [50000, 60000, 70000, 55000],
    'department': ['Engineering', 'Sales', 'Engineering', 'Marketing']
})

# Data exploration
print(df.info())
print(df.describe())
print(df.head())

# Data filtering and selection
engineers = df[df['department'] == 'Engineering']
high_earners = df[df['salary'] > 55000]
subset = df[['name', 'salary']].copy()

# Grouping and aggregation
dept_stats = df.groupby('department').agg({
    'salary': ['mean', 'min', 'max'],
    'age': 'mean'
}).round(2)

# Data cleaning
df_clean = df.dropna()  # Remove missing values
df['salary_scaled'] = (df['salary'] - df['salary'].mean()) / df['salary'].std()
\`\`\`

### Matplotlib & Seaborn - Visualization
\`\`\`python
import matplotlib.pyplot as plt
import seaborn as sns

# Set style
plt.style.use('seaborn-v0_8')
sns.set_palette("husl")

# Basic plots
fig, axes = plt.subplots(2, 2, figsize=(12, 10))

# Histogram
axes[0, 0].hist(df['salary'], bins=10, alpha=0.7)
axes[0, 0].set_title('Salary Distribution')

# Scatter plot
axes[0, 1].scatter(df['age'], df['salary'], alpha=0.7)
axes[0, 1].set_xlabel('Age')
axes[0, 1].set_ylabel('Salary')

# Box plot
sns.boxplot(data=df, x='department', y='salary', ax=axes[1, 0])
axes[1, 0].set_title('Salary by Department')

# Correlation heatmap
numeric_df = df.select_dtypes(include=[np.number])
sns.heatmap(numeric_df.corr(), annot=True, ax=axes[1, 1])

plt.tight_layout()
plt.show()
\`\`\`

## Data Loading and Processing

### Reading Different Data Formats
\`\`\`python
# CSV files
df_csv = pd.read_csv('data.csv')

# Excel files
df_excel = pd.read_excel('data.xlsx', sheet_name='Sheet1')

# JSON files
df_json = pd.read_json('data.json')

# Database connections
from sqlalchemy import create_engine
engine = create_engine('postgresql://user:password@host:port/database')
df_db = pd.read_sql('SELECT * FROM table_name', engine)

# API data
import requests
response = requests.get('https://api.example.com/data')
df_api = pd.DataFrame(response.json())
\`\`\`

### Data Cleaning Pipeline
\`\`\`python
def clean_data(df):
    """Comprehensive data cleaning pipeline"""
    # Create a copy
    df_clean = df.copy()
    
    # Handle missing values
    # Option 1: Drop rows with any missing values
    # df_clean = df_clean.dropna()
    
    # Option 2: Fill missing values
    numeric_columns = df_clean.select_dtypes(include=[np.number]).columns
    categorical_columns = df_clean.select_dtypes(include=['object']).columns
    
    # Fill numeric with median
    df_clean[numeric_columns] = df_clean[numeric_columns].fillna(
        df_clean[numeric_columns].median()
    )
    
    # Fill categorical with mode
    for col in categorical_columns:
        df_clean[col] = df_clean[col].fillna(df_clean[col].mode()[0])
    
    # Remove duplicates
    df_clean = df_clean.drop_duplicates()
    
    # Handle outliers (IQR method)
    for col in numeric_columns:
        Q1 = df_clean[col].quantile(0.25)
        Q3 = df_clean[col].quantile(0.75)
        IQR = Q3 - Q1
        lower_bound = Q1 - 1.5 * IQR
        upper_bound = Q3 + 1.5 * IQR
        df_clean = df_clean[
            (df_clean[col] >= lower_bound) & 
            (df_clean[col] <= upper_bound)
        ]
    
    return df_clean

# Apply cleaning
df_processed = clean_data(df)
\`\`\`

## Exploratory Data Analysis (EDA)

### Statistical Summary
\`\`\`python
def perform_eda(df):
    """Comprehensive EDA function"""
    print("Dataset Shape:", df.shape)
    print("\\nData Types:")
    print(df.dtypes)
    
    print("\\nMissing Values:")
    missing = df.isnull().sum()
    print(missing[missing > 0])
    
    print("\\nStatistical Summary:")
    print(df.describe(include='all'))
    
    # Correlation analysis
    numeric_df = df.select_dtypes(include=[np.number])
    if len(numeric_df.columns) > 1:
        correlation_matrix = numeric_df.corr()
        print("\\nHighest Correlations:")
        correlation_pairs = []
        for i in range(len(correlation_matrix.columns)):
            for j in range(i+1, len(correlation_matrix.columns)):
                col1 = correlation_matrix.columns[i]
                col2 = correlation_matrix.columns[j]
                corr_val = correlation_matrix.iloc[i, j]
                correlation_pairs.append((col1, col2, corr_val))
        
        correlation_pairs.sort(key=lambda x: abs(x[2]), reverse=True)
        for col1, col2, corr in correlation_pairs[:5]:
            print(f"{col1} - {col2}: {corr:.3f}")

perform_eda(df)
\`\`\``,
        practice: {
          task: 'Analyze a real-world dataset (e.g., house prices, customer churn) and create a comprehensive EDA report',
          starter: `import pandas as pd
import numpy as np
import matplotlib.pyplot as plt
import seaborn as sns

# Load your dataset
df = pd.read_csv('your_dataset.csv')

# Perform comprehensive EDA
def analyze_dataset(df):
    # Add your analysis code here
    pass

# Create visualizations
def create_visualizations(df):
    # Add your visualization code here
    pass

# Generate insights
def generate_insights(df):
    # Add your insights here
    pass`,
          hints: [
            'Start with basic dataset information (shape, types, missing values)',
            'Create distribution plots for numerical variables',
            'Analyze categorical variables with value counts',
            'Look for correlations between variables',
            'Identify outliers and unusual patterns',
            'Generate actionable business insights from the data'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'data-analysis-1',
              question: 'Which pandas method is best for getting a statistical summary of a dataset?',
              options: ['info()', 'describe()', 'head()', 'shape()'],
              correctAnswer: 1,
              difficulty: 'easy' as const,
              explanation: 'The describe() method provides statistical summary including count, mean, std, min, max, and quartiles.',
              points: 10
            }
          ],
          passThreshold: 75
        }
      }
    ],
    type: 'data-science'
  },
  {
    id: 'machine-learning-production',
    title: 'Machine Learning in Production',
    description: 'Deploy and scale machine learning models in production environments with MLOps best practices.',
    level: 'advanced',
    totalDuration: '12-15 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Machine learning fundamentals', 'Python proficiency', 'Docker basics'],
    learningOutcomes: [
      'Build production-ready ML pipelines',
      'Implement model versioning and monitoring',
      'Deploy models using various strategies',
      'Set up automated training and deployment',
      'Monitor model performance and drift',
      'Implement A/B testing for models'
    ],
    modules: [
      {
        id: 'mlops-fundamentals',
        title: 'MLOps Fundamentals',
        description: 'Introduction to Machine Learning Operations and production deployment strategies',
        duration: '120 minutes',
        difficulty: 'advanced',
        objectives: [
          'Understand MLOps principles and practices',
          'Design scalable ML architectures',
          'Implement CI/CD for machine learning',
          'Set up model monitoring and alerting'
        ],
        content: `# MLOps Fundamentals

## MLOps Overview

MLOps (Machine Learning Operations) is the practice of applying DevOps principles to machine learning workflows.

### Key Components:
- **Version Control**: Code, data, and model versioning
- **Automated Pipelines**: Training, testing, and deployment automation
- **Monitoring**: Model performance and data drift detection
- **Governance**: Model lifecycle management and compliance

## Production ML Pipeline

### Model Training Pipeline
\`\`\`python
# training_pipeline.py
import mlflow
import pandas as pd
from sklearn.ensemble import RandomForestClassifier
from sklearn.model_selection import train_test_split
from sklearn.metrics import accuracy_score, classification_report

class ModelTrainer:
    def __init__(self, experiment_name="production_model"):
        mlflow.set_experiment(experiment_name)
        
    def train_model(self, data_path, target_column):
        with mlflow.start_run():
            # Load and prepare data
            df = pd.read_csv(data_path)
            X = df.drop(columns=[target_column])
            y = df[target_column]
            
            X_train, X_test, y_train, y_test = train_test_split(
                X, y, test_size=0.2, random_state=42
            )
            
            # Train model
            model = RandomForestClassifier(
                n_estimators=100,
                max_depth=10,
                random_state=42
            )
            model.fit(X_train, y_train)
            
            # Evaluate model
            predictions = model.predict(X_test)
            accuracy = accuracy_score(y_test, predictions)
            
            # Log parameters and metrics
            mlflow.log_param("n_estimators", 100)
            mlflow.log_param("max_depth", 10)
            mlflow.log_metric("accuracy", accuracy)
            
            # Log model
            mlflow.sklearn.log_model(
                model, 
                "model",
                registered_model_name="production_classifier"
            )
            
            return model, accuracy

# Usage
trainer = ModelTrainer()
model, accuracy = trainer.train_model("data.csv", "target")
print(f"Model trained with accuracy: {accuracy}")
\`\`\`

### Model Serving with FastAPI
\`\`\`python
# model_server.py
from fastapi import FastAPI, HTTPException
from pydantic import BaseModel
import mlflow.sklearn
import pandas as pd
import numpy as np
from typing import List

app = FastAPI(title="ML Model API", version="1.0.0")

# Load model at startup
model = None

@app.on_event("startup")
async def load_model():
    global model
    model_uri = "models:/production_classifier/Production"
    model = mlflow.sklearn.load_model(model_uri)

class PredictionRequest(BaseModel):
    features: List[float]

class PredictionResponse(BaseModel):
    prediction: int
    probability: float
    model_version: str

@app.post("/predict", response_model=PredictionResponse)
async def predict(request: PredictionRequest):
    try:
        # Prepare input
        features = np.array(request.features).reshape(1, -1)
        
        # Make prediction
        prediction = model.predict(features)[0]
        probabilities = model.predict_proba(features)[0]
        confidence = max(probabilities)
        
        return PredictionResponse(
            prediction=int(prediction),
            probability=float(confidence),
            model_version="1.0.0"
        )
    except Exception as e:
        raise HTTPException(status_code=400, detail=str(e))

@app.get("/health")
async def health_check():
    return {"status": "healthy", "model_loaded": model is not None}
\`\`\`

### Model Monitoring
\`\`\`python
# monitoring.py
import pandas as pd
import numpy as np
from scipy import stats
from datetime import datetime, timedelta
import logging

class ModelMonitor:
    def __init__(self, reference_data, threshold=0.05):
        self.reference_data = reference_data
        self.threshold = threshold
        self.logger = logging.getLogger(__name__)
        
    def detect_data_drift(self, current_data):
        """Detect data drift using KS test"""
        drift_detected = {}
        
        for column in self.reference_data.columns:
            if column in current_data.columns:
                # Perform Kolmogorov-Smirnov test
                ks_stat, p_value = stats.ks_2samp(
                    self.reference_data[column],
                    current_data[column]
                )
                
                drift_detected[column] = {
                    'ks_statistic': ks_stat,
                    'p_value': p_value,
                    'drift': p_value < self.threshold
                }
                
                if p_value < self.threshold:
                    self.logger.warning(f"Data drift detected in {column}")
        
        return drift_detected
    
    def monitor_model_performance(self, predictions, actuals):
        """Monitor model performance metrics"""
        accuracy = (predictions == actuals).mean()
        
        # Log performance
        self.logger.info(f"Current accuracy: {accuracy:.3f}")
        
        # Alert if performance drops significantly
        if accuracy < 0.8:  # Example threshold
            self.logger.error("Model performance degraded!")
            
        return {
            'accuracy': accuracy,
            'timestamp': datetime.now(),
            'predictions_count': len(predictions)
        }

# Usage in production
monitor = ModelMonitor(reference_data=training_data)

# Check for drift with new data
drift_results = monitor.detect_data_drift(new_data)

# Monitor performance
performance = monitor.monitor_model_performance(predictions, actual_labels)
\`\`\`

## Automated ML Pipeline with GitHub Actions

### CI/CD Configuration
\`\`\`yaml
# .github/workflows/ml-pipeline.yml
name: ML Pipeline

on:
  push:
    branches: [ main ]
  pull_request:
    branches: [ main ]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
    - uses: actions/checkout@v2
    
    - name: Set up Python
      uses: actions/setup-python@v2
      with:
        python-version: 3.9
        
    - name: Install dependencies
      run: |
        pip install -r requirements.txt
        
    - name: Run tests
      run: |
        pytest tests/
        
    - name: Run data validation
      run: |
        python scripts/validate_data.py
        
  train:
    needs: test
    runs-on: ubuntu-latest
    if: github.ref == 'refs/heads/main'
    steps:
    - uses: actions/checkout@v2
    
    - name: Train model
      run: |
        python training_pipeline.py
        
    - name: Validate model
      run: |
        python scripts/validate_model.py
        
  deploy:
    needs: train
    runs-on: ubuntu-latest
    steps:
    - name: Deploy to staging
      run: |
        docker build -t ml-model:latest .
        docker push registry/ml-model:latest
        
    - name: Run integration tests
      run: |
        python tests/integration_tests.py
\`\`\``,
        practice: {
          task: 'Build a complete MLOps pipeline for a classification model with automated training, testing, and deployment',
          starter: `# Create a production-ready ML pipeline that includes:
# 1. Automated data validation
# 2. Model training with experiment tracking
# 3. Model evaluation and validation
# 4. Automated deployment
# 5. Model monitoring and alerting

import mlflow
import pandas as pd
from sklearn.ensemble import RandomForestClassifier

class ProductionMLPipeline:
    def __init__(self):
        # Initialize your pipeline
        pass
    
    def validate_data(self, data):
        # Add data validation logic
        pass
    
    def train_model(self, data):
        # Add training logic
        pass
    
    def deploy_model(self, model):
        # Add deployment logic
        pass`,
          hints: [
            'Use MLflow for experiment tracking and model registry',
            'Implement data quality checks and validation',
            'Set up automated model evaluation metrics',
            'Create a model serving API with health checks',
            'Implement monitoring for data drift and model performance',
            'Use Docker for containerized deployment'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'mlops-1',
              question: 'What is the primary purpose of model monitoring in production?',
              options: [
                'To improve model accuracy',
                'To detect data drift and performance degradation',
                'To reduce computational costs',
                'To automate model training'
              ],
              correctAnswer: 1,
              difficulty: 'medium' as const,
              explanation: 'Model monitoring helps detect when models degrade due to data drift or changing conditions.',
              points: 15
            }
          ],
          passThreshold: 75
        }
      }
    ],
    type: 'data-science'
  }
];